import React from 'react';
import {
  ChevronDown,
  ChevronRight,
  MessageCircle,
  PanelLeft,
  Pencil,
  Search,
  X,
} from 'lucide-react';
import arrowBrandDark from '../../assets/arrow-brand-dark.png';
import arrowBrandLight from '../../assets/arrow-brand-light.png';
import { useTheme } from '../../contexts/ThemeContext';
import { useMultiSelect } from '../../hooks/sidebar/useMultiSelect';
import { useChatOperations } from '../../hooks/sidebar/useChatOperations';
import { useContextMenu } from '../../hooks/sidebar/useContextMenu';
import { useModalState } from '../../hooks/sidebar/useModalState';
import { useChatFiltering } from '../../hooks/sidebar/useChatFiltering';
import SidebarHeader from '../SidebarHeader';
import ArchivedChatsButton from '../ArchivedChatsButton';
import MultiSelectToolbar from '../MultiSelectToolbar';
import ChatItem from '../ChatItem';
import ContextMenu from '../ContextMenu';
import DeleteModal from '../DeleteModal';
import ProgressModal from '../ProgressModal';
import UserProfile from '../UserProfile';
import ProjectMoveModal from '../projects/ProjectMoveModal';
import ProjectWorkspaceModal from '../projects/ProjectWorkspaceModal';
import ProjectWorkspaceSection from '../projects/ProjectWorkspaceSection';
import MoneyBagIcon from '../charity/MoneyBagIcon';
import GivingModal from '../charity/GivingModal';
import './ChatSidebar.css';

const getChatProjectId = (chat = {}) => chat.projectId || chat.project_id || null;
const WORKSPACES_COLLAPSED_KEY = 'arrowai-sidebar-workspaces-collapsed';
const RECENTS_COLLAPSED_KEY = 'arrowai-sidebar-recents-collapsed';

const getDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getChatDate = (chat = {}) =>
  getDateValue(chat.lastMessageSent) ||
  getDateValue(chat.updatedAt) ||
  getDateValue(chat.createdAt);

const getRecentPreviewTexts = (chat = {}) => {
  const previews = Array.isArray(chat.recentMessagePreviews)
    ? chat.recentMessagePreviews
    : [];

  return previews
    .map((preview) => {
      if (typeof preview === 'string') return preview;
      return preview?.text || '';
    })
    .filter(Boolean);
};

const getChatPreview = (chat = {}) =>
  chat.lastMessagePreview ||
  getRecentPreviewTexts(chat)[0] ||
  chat.preview ||
  chat.summary ||
  chat.lastMessage ||
  chat.model ||
  '';

const getChatSearchIndexText = (chat = {}) =>
  chat.searchIndexText ||
  [
    chat.topic,
    getChatPreview(chat),
    ...getRecentPreviewTexts(chat),
    chat.projectName,
    chat.model,
    chat.summary,
  ]
    .filter(Boolean)
    .join(' ');

const stripMarkdownFormatting = (value = '') =>
  String(value || '')
    .replace(/!\[([^\]]*)\]\((?:<[^>]+>|[^)]+)\)/g, '$1')
    .replace(/\[([^\]]+)\]\((?:<[^>]+>|[^)]+)\)/g, '$1')
    .replace(/!\[([^\]]*)\]\(<[^>\s]+>?[^)]*\)?/g, '$1')
    .replace(/<https?:\/\/[^>\s]+>/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/(^|\s)[#>*-]{1,3}\s+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeSearchText = (value) =>
  stripMarkdownFormatting(value).trim().toLowerCase();

const renderHighlightedText = (value, query) => {
  const text = stripMarkdownFormatting(value);
  const normalizedQuery = normalizeSearchText(query);

  if (!text || !normalizedQuery) return text;

  const lowerText = text.toLowerCase();
  const parts = [];
  let cursor = 0;
  let matchIndex = lowerText.indexOf(normalizedQuery, cursor);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(text.slice(cursor, matchIndex));
    }
    parts.push(
      <span className="arrow-search-highlight" key={`${matchIndex}-${parts.length}`}>
        {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
      </span>
    );
    cursor = matchIndex + normalizedQuery.length;
    matchIndex = lowerText.indexOf(normalizedQuery, cursor);
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
};

const formatSearchDate = (date) => {
  if (!date) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);

  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff > 1 && dayDiff < 7) {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(date);
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const getRecentSectionLabel = (chats = []) => {
  if (!chats.length) return 'Previous 7 Days';
  const hasOlderChat = chats.some((chat) => {
    const date = getChatDate(chat);
    if (!date) return false;
    return Date.now() - date.getTime() > 7 * 86400000;
  });

  return hasOlderChat ? 'Recent chats' : 'Previous 7 Days';
};

const getMessageSearchText = (messagesByChatId = {}, chatId) => {
  const messages = messagesByChatId?.[chatId];
  if (!Array.isArray(messages)) return '';

  return messages
    .map((message) => message?.text || message?.reasoning_text || '')
    .filter(Boolean)
    .join(' ');
};

const getBestSnippet = ({ chat, query, messagesByChatId }) => {
  const preview = [
    getChatPreview(chat),
    ...getRecentPreviewTexts(chat),
    chat.searchIndexText,
  ]
    .filter(Boolean)
    .join(' ');
  const messagesText = getMessageSearchText(messagesByChatId, chat.id);
  const source = preview || messagesText;
  if (!source) return '';

  const normalizedQuery = normalizeSearchText(query);
  const sourceText = stripMarkdownFormatting(source);
  const matchIndex = normalizedQuery
    ? sourceText.toLowerCase().indexOf(normalizedQuery)
    : -1;
  const start = matchIndex > 24 ? matchIndex - 24 : 0;
  const snippet = sourceText.slice(start, start + 148);
  return `${start > 0 ? '...' : ''}${snippet}${start + 148 < sourceText.length ? '...' : ''}`;
};

const ChatSearchModal = ({
  isOpen,
  chats,
  messagesByChatId,
  onClose,
  onNewChat,
  onChatSelect,
}) => {
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef(null);
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    setQuery('');
    setActiveIndex(0);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const searchableChats = React.useMemo(
    () => chats.filter((chat) => !chat.archived),
    [chats]
  );

  const resultChats = React.useMemo(() => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return searchableChats.slice(0, 6);

    return searchableChats
      .map((chat) => {
        const title = chat.topic || 'Untitled Chat';
        const haystack = [
          title,
          getChatSearchIndexText(chat),
          chat.projectName,
          chat.model,
          getMessageSearchText(messagesByChatId, chat.id),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(normalizedQuery)) return null;

        const titleRank = title.toLowerCase().includes(normalizedQuery) ? 0 : 1;
        const date = getChatDate(chat);
        return { chat, titleRank, timestamp: date?.getTime() || 0 };
      })
      .filter(Boolean)
      .sort((a, b) => a.titleRank - b.titleRank || b.timestamp - a.timestamp)
      .map((item) => item.chat)
      .slice(0, 24);
  }, [messagesByChatId, query, searchableChats]);

  const rows = React.useMemo(() => {
    const chatRows = resultChats.map((chat) => ({ type: 'chat', chat }));
    return normalizeSearchText(query)
      ? chatRows
      : [{ type: 'new-chat' }, ...chatRows];
  }, [query, resultChats]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const closeAndSelectChat = (chatId) => {
    onClose();
    onChatSelect?.(chatId);
  };

  const closeAndStartChat = () => {
    onClose();
    onNewChat?.();
  };

  const runRowAction = (row) => {
    if (!row) return;
    if (row.type === 'new-chat') {
      closeAndStartChat();
      return;
    }
    closeAndSelectChat(row.chat.id);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(rows.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      runRowAction(rows[activeIndex]);
    }
  };

  if (!isOpen) return null;

  const hasQuery = Boolean(normalizeSearchText(query));
  const sectionLabel = hasQuery ? '' : getRecentSectionLabel(resultChats);

  return (
    <div
      className="arrow-search-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (panelRef.current && !panelRef.current.contains(event.target)) {
          onClose();
        }
      }}
    >
      <div
        className="arrow-search-panel theme-panel-bg theme-text-primary theme-border"
        role="dialog"
        aria-modal="true"
        aria-label="Search chats"
        ref={panelRef}
      >
        <div className="arrow-search-input-row">
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            className="arrow-search-input"
            placeholder="Search chats..."
            aria-label="Search chats"
          />
          <button
            type="button"
            onClick={onClose}
            className="arrow-search-close"
            aria-label="Close search"
            title="Close search"
          >
            <X size={19} />
          </button>
        </div>

        <div className="arrow-search-results" role="listbox" aria-label="Chat search results">
          {!hasQuery && (
            <button
              type="button"
              className={`arrow-search-row arrow-search-new-chat ${activeIndex === 0 ? 'active' : ''}`}
              onMouseEnter={() => setActiveIndex(0)}
              onClick={closeAndStartChat}
            >
              <span className="arrow-search-row-icon">
                <Pencil size={18} />
              </span>
              <span>New chat</span>
            </button>
          )}

          {!hasQuery && resultChats.length > 0 && (
            <div className="arrow-search-section-label">{sectionLabel}</div>
          )}

          {resultChats.length > 0 ? (
            resultChats.map((chat, index) => {
              const rowIndex = hasQuery ? index : index + 1;
              const date = formatSearchDate(getChatDate(chat));
              const snippet = getBestSnippet({ chat, query, messagesByChatId });
              const title = chat.topic || 'Untitled Chat';

              return (
                <button
                  type="button"
                  key={chat.id}
                  className={`arrow-search-row ${hasQuery ? 'arrow-search-row-result' : ''} ${activeIndex === rowIndex ? 'active' : ''}`}
                  onMouseEnter={() => setActiveIndex(rowIndex)}
                  onClick={() => closeAndSelectChat(chat.id)}
                  role="option"
                  aria-selected={activeIndex === rowIndex}
                >
                  <span className="arrow-search-row-icon">
                    <MessageCircle size={18} />
                  </span>
                  <span className="arrow-search-row-body">
                    <span className="arrow-search-row-title">
                      {renderHighlightedText(title, query)}
                    </span>
                    {hasQuery && snippet && (
                      <span className="arrow-search-row-snippet">
                        {renderHighlightedText(snippet, query)}
                      </span>
                    )}
                  </span>
                  {hasQuery && date && (
                    <span className="arrow-search-row-date">{date}</span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="arrow-search-empty">
              {hasQuery ? 'No chats found' : 'No recent chats'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatSidebar = ({ 
  isOpen, 
  chats, 
  projects = [],
  activeProjectId = null,
  isLoadingProjects = false,
  projectError = '',
  onChatSelect, 
  onProjectSelect,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onMoveChatToProject,
  onNewChat,
  toggleSidebar,
  selectedChatId, 
  hasSubscription, 
  hasBasicSubscription,
  onUpgrade, 
  userName, 
  userEmail, 
  onSettingsClick,
  isMobile,
  viewMode = 'detailed',
  messagesByChatId = {}
}) => {
  const { glowEnabled } = useTheme();
  const [projectModalState, setProjectModalState] = React.useState({
    isOpen: false,
    project: null,
  });
  const [moveModalChatId, setMoveModalChatId] = React.useState(null);
  const [workspacesCollapsed, setWorkspacesCollapsed] = React.useState(
    () => localStorage.getItem(WORKSPACES_COLLAPSED_KEY) === 'true'
  );
  const [recentsCollapsed, setRecentsCollapsed] = React.useState(
    () => localStorage.getItem(RECENTS_COLLAPSED_KEY) === 'true'
  );
  const [expandedProjectChatIds, setExpandedProjectChatIds] = React.useState(() => new Set());
  const [isRecentsChevronVisible, setIsRecentsChevronVisible] = React.useState(false);
  const [isRecentsFlyoutOpen, setIsRecentsFlyoutOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isGivingOpen, setIsGivingOpen] = React.useState(false);
  const flyoutRef = React.useRef(null);
  
  // Custom hooks for different concerns
  const { showingArchived, setShowingArchived, archivedChats, displayedChats } = useChatFiltering(chats);
  const { contextMenu, hideContextMenu, handleContextMenu, handleMenuClick } = useContextMenu();
  const { shareChat, archiveChat, unarchiveChat, renameChat } = useChatOperations(chats);
  const {
    deleteModal,
    progressModal,
    editingChatId,
    editingValue,
    setProgressModal,
    setEditingValue,
    showDeleteModal,
    hideDeleteModal,
    confirmDeleteChat,
    startRenaming,
    handleSaveRename,
    handleRenameKeyDown
  } = useModalState(chats);
  const {
    isSelectMode,
    selectedChatIds,
    toggleSelectMode: baseToggleSelectMode,
    toggleChatSelection,
    selectAllChats: baseSelectAllChats,
    deselectAllChats,
    exitSelectMode,
    bulkArchiveChats,
    bulkUnarchiveChats,
    bulkDeleteChats: baseBulkDeleteChats
  } = useMultiSelect();

  // Wrapper functions to integrate with custom hooks
  const toggleSelectMode = () => {
    baseToggleSelectMode();
    hideContextMenu();
  };
  
  const projectChats = React.useMemo(
    () =>
      activeProjectId
        ? displayedChats.filter((chat) => getChatProjectId(chat) === activeProjectId)
        : [],
    [activeProjectId, displayedChats]
  );
  const recentChats = React.useMemo(
    () => displayedChats.filter((chat) => !getChatProjectId(chat)),
    [displayedChats]
  );
  const visibleChats = React.useMemo(
    () => [...projectChats, ...recentChats],
    [projectChats, recentChats]
  );

  React.useEffect(() => {
    localStorage.setItem(WORKSPACES_COLLAPSED_KEY, workspacesCollapsed ? 'true' : 'false');
  }, [workspacesCollapsed]);

  React.useEffect(() => {
    localStorage.setItem(RECENTS_COLLAPSED_KEY, recentsCollapsed ? 'true' : 'false');
  }, [recentsCollapsed]);

  React.useEffect(() => {
    setExpandedProjectChatIds(new Set());
  }, [activeProjectId]);

  React.useEffect(() => {
    if (isOpen || !isRecentsFlyoutOpen) return undefined;

    const handlePointerDown = (event) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target)) {
        setIsRecentsFlyoutOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, isRecentsFlyoutOpen]);

  const recentsHoverTargetProps = {
    onMouseEnter: () => setIsRecentsChevronVisible(true),
    onMouseLeave: () => setIsRecentsChevronVisible(false),
    onFocus: () => setIsRecentsChevronVisible(true),
    onBlur: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsRecentsChevronVisible(false);
      }
    },
  };

  const selectAllChats = () => baseSelectAllChats(visibleChats);
  const bulkDeleteChats = () => baseBulkDeleteChats(setProgressModal);

  // Wrapper functions that add hide context menu behavior
  const handleShareChat = (chatId) => {
    hideContextMenu();
    shareChat(chatId);
  };
  
  const handleArchiveChat = (chatId) => {
    hideContextMenu();
    archiveChat(chatId);
  };
  
  const handleUnarchiveChat = (chatId) => {
    hideContextMenu();
    unarchiveChat(chatId);
  };
  
  const handleStartRenaming = (chatId) => {
    hideContextMenu();
    startRenaming(chatId);
  };
  
  const handleShowDeleteModal = (chatId) => {
    hideContextMenu();
    showDeleteModal(chatId);
  };
  
  const handleSettingsClick = () => {
    onSettingsClick();
  };

  const handleNewChatClick = () => {
    setIsRecentsFlyoutOpen(false);
    onNewChat?.();
  };

  const handleSearchClick = () => {
    setIsRecentsFlyoutOpen(false);
    setIsSearchOpen(true);
  };

  const handleCollapsedChatSelect = (chatId) => {
    setIsRecentsFlyoutOpen(false);
    onChatSelect?.(chatId);
  };

  const handleGivingClick = () => {
    setIsRecentsFlyoutOpen(false);
    setIsGivingOpen(true);
  };

  const openCreateProjectModal = () => {
    setProjectModalState({ isOpen: true, project: null });
  };

  const openEditProjectModal = (project) => {
    setProjectModalState({ isOpen: true, project });
  };

  const closeProjectModal = () => {
    setProjectModalState({ isOpen: false, project: null });
  };

  const handleProjectSave = async (payload) => {
    if (payload.projectId) {
      return onUpdateProject?.(payload);
    }
    return onCreateProject?.(payload);
  };

  const handleShowMoveModal = (chatId) => {
    hideContextMenu();
    setMoveModalChatId(chatId);
  };

  const toggleProjectChats = (projectId) => {
    setExpandedProjectChatIds((current) => {
      const next = new Set(current);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const movingChat = chats.find((chat) => chat.id === moveModalChatId) || null;
  const renderChatItem = (chat, nested = false, overrideSelect = onChatSelect) => (
    <ChatItem
      key={chat.id}
      chat={chat}
      selectedChatId={selectedChatId}
      contextMenu={contextMenu}
      isSelectMode={isSelectMode}
      selectedChatIds={selectedChatIds}
      viewMode={nested ? 'compact' : viewMode}
      editingChatId={editingChatId}
      editingValue={editingValue}
      onChatSelect={overrideSelect}
      handleContextMenu={handleContextMenu}
      toggleChatSelection={toggleChatSelection}
      handleMenuClick={handleMenuClick}
      setEditingValue={setEditingValue}
      handleSaveRename={(chatId) => handleSaveRename(chatId, renameChat)}
      handleRenameKeyDown={(e, chatId) => handleRenameKeyDown(e, chatId, renameChat)}
    />
  );

  const collapsedRailButtonClass = 'arrow-sidebar-rail-button';
  const recentsFlyoutChats = recentChats.slice(0, 10);

  return (
    <div
      className={`arrow-sidebar-shell theme-sidebar-bg theme-text-primary h-full transition-all duration-300 ease-in-out ${glowEnabled ? 'sidebar-neon-glow' : ''} ${isOpen ? 'arrow-sidebar-expanded' : 'arrow-sidebar-collapsed'}`}
      style={{ width: isOpen ? '320px' : '56px' }}
    >
      <div
        className={`arrow-sidebar-expanded-content ${isOpen ? 'open' : 'closed'}`}
        aria-hidden={!isOpen}
      >
          <div className="arrow-sidebar-brand-row">
            <div className="arrow-sidebar-brand">
              <span>ArrowAI</span>
            </div>
            <button
              type="button"
              onClick={toggleSidebar}
              className="arrow-sidebar-header-button"
              title="Hide sidebar"
              aria-label="Hide sidebar"
            >
              <PanelLeft size={20} />
            </button>
          </div>

          <div className="arrow-sidebar-primary-actions">
            <button
              type="button"
              onClick={handleNewChatClick}
              className="arrow-sidebar-primary-row theme-sidebar-item"
            >
              <span className="arrow-sidebar-primary-icon">
                <Pencil size={19} />
              </span>
              <span>New chat</span>
            </button>
            <button
              type="button"
              onClick={handleSearchClick}
              className="arrow-sidebar-primary-row theme-sidebar-item"
            >
              <span className="arrow-sidebar-primary-icon">
                <Search size={19} />
              </span>
              <span>Search chats</span>
            </button>
          </div>

          <div className={`arrow-sidebar-scroll flex-grow overflow-y-auto ${isMobile ? '' : ''}`}>
            <div className="px-3 pb-4">
              <ProjectWorkspaceSection
                projects={projects}
                activeProjectId={activeProjectId}
                activeProjectChats={projectChats}
                expandedProjectChatIds={expandedProjectChatIds}
                collapsed={workspacesCollapsed}
                isLoading={isLoadingProjects}
                error={projectError}
                onProjectSelect={onProjectSelect}
                onCreateProject={openCreateProjectModal}
                onEditProject={openEditProjectModal}
                onToggleCollapsed={() => setWorkspacesCollapsed((prev) => !prev)}
                onToggleProjectChats={toggleProjectChats}
                renderProjectChat={(chat) => renderChatItem(chat, true)}
              />

              <SidebarHeader 
                showingArchived={showingArchived}
                setShowingArchived={setShowingArchived}
              />
              
              <MultiSelectToolbar 
                isSelectMode={isSelectMode}
                selectedChatIds={selectedChatIds}
                exitSelectMode={exitSelectMode}
                selectAllChats={selectAllChats}
                deselectAllChats={deselectAllChats}
                showingArchived={showingArchived}
                bulkArchiveChats={bulkArchiveChats}
                bulkUnarchiveChats={bulkUnarchiveChats}
                bulkDeleteChats={bulkDeleteChats}
              />

              <div className="arrow-sidebar-section mt-4">
                <button
                  type="button"
                  onClick={() => setRecentsCollapsed((prev) => !prev)}
                  className="arrow-sidebar-section-header arrow-sidebar-hover-target"
                  {...recentsHoverTargetProps}
                >
                  <span className="truncate">{showingArchived ? 'Archived' : 'Recents'}</span>
                  <span
                    className={`arrow-sidebar-section-chevron ${isRecentsChevronVisible ? 'visible' : ''}`}
                    aria-hidden="true"
                  >
                    {recentsCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                  </span>
                  <span className="arrow-sidebar-count">{recentChats.length}</span>
                </button>

                {!recentsCollapsed && (
                  <div className="arrow-sidebar-list">
                    {recentChats.length > 0 ? (
                      recentChats.map((chat) => (
                        <div key={chat.id} {...recentsHoverTargetProps}>
                          {renderChatItem(chat, false)}
                        </div>
                      ))
                    ) : (
                      <div className="arrow-sidebar-empty">
                        {showingArchived ? 'No archived chats' : 'No recent chats'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <ArchivedChatsButton 
                showingArchived={showingArchived}
                setShowingArchived={setShowingArchived}
                archivedChats={archivedChats}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleGivingClick}
            className="arrow-sidebar-giving-row theme-sidebar-item"
            title="This month's charity vote"
          >
            <span className="arrow-sidebar-giving-icon">
              <MoneyBagIcon size={19} />
            </span>
            <span className="arrow-sidebar-giving-label">Giving</span>
            <span className="arrow-sidebar-giving-badge">Vote</span>
          </button>

          <UserProfile 
            userName={userName}
            userEmail={userEmail}
            hasSubscription={hasSubscription}
            hasBasicSubscription={hasBasicSubscription}
            onUpgrade={onUpgrade}
            handleSettingsClick={handleSettingsClick}
            isMobile={isMobile}
          />
      </div>

      <div
        className={`arrow-sidebar-rail ${isOpen ? 'open' : 'closed'}`}
        ref={flyoutRef}
        aria-hidden={isOpen}
      >
        <div className="arrow-sidebar-rail-top">
          <button
            type="button"
            onClick={toggleSidebar}
            className={`${collapsedRailButtonClass} arrow-sidebar-rail-toggle`}
            title="Show sidebar"
            aria-label="Show sidebar"
          >
            <span className="arrow-sidebar-rail-toggle-logo" aria-hidden="true">
              <img
                src={arrowBrandLight}
                alt=""
                className="arrow-sidebar-rail-brand-mark arrow-sidebar-brand-mark-light"
              />
              <img
                src={arrowBrandDark}
                alt=""
                className="arrow-sidebar-rail-brand-mark arrow-sidebar-brand-mark-dark"
              />
            </span>
            <span className="arrow-sidebar-rail-toggle-icon" aria-hidden="true">
              <PanelLeft size={20} />
            </span>
          </button>
          <button
            type="button"
            onClick={handleNewChatClick}
            className={collapsedRailButtonClass}
            title="New chat"
            aria-label="New chat"
          >
            <Pencil size={19} />
          </button>
          <button
            type="button"
            onClick={handleSearchClick}
            className={collapsedRailButtonClass}
            title="Search chats"
            aria-label="Search chats"
          >
            <Search size={19} />
          </button>
          <button
            type="button"
            onClick={() => setIsRecentsFlyoutOpen((current) => !current)}
            className={`${collapsedRailButtonClass} ${isRecentsFlyoutOpen ? 'active' : ''}`}
            title="Recents"
            aria-label="Recents"
            aria-expanded={isRecentsFlyoutOpen}
          >
            <MessageCircle size={19} />
          </button>
          <button
            type="button"
            onClick={handleGivingClick}
            className={`${collapsedRailButtonClass} arrow-sidebar-rail-giving`}
            title="This month's charity vote"
            aria-label="This month's charity vote"
          >
            <MoneyBagIcon size={19} />
          </button>
        </div>

        {isRecentsFlyoutOpen && (
          <div className="arrow-sidebar-recents-flyout theme-card-bg theme-text-primary theme-border">
            <div className="arrow-sidebar-recents-flyout-title">
              Recents
            </div>
            <div className="arrow-sidebar-recents-flyout-list">
              {recentsFlyoutChats.length > 0 ? (
                recentsFlyoutChats.map((chat) => (
                  <div key={chat.id}>
                    {renderChatItem(chat, false, handleCollapsedChatSelect)}
                  </div>
                ))
              ) : (
                <div className="arrow-sidebar-empty">
                  No recent chats
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleSettingsClick}
          className={`${collapsedRailButtonClass} arrow-sidebar-rail-avatar`}
          title={userName || 'Account'}
          aria-label="Account settings"
        >
          {(userName || userEmail || 'A').trim().charAt(0).toUpperCase()}
        </button>
      </div>

      <ContextMenu 
        contextMenu={contextMenu}
        showingArchived={showingArchived}
        toggleSelectMode={toggleSelectMode}
        shareChat={handleShareChat}
        startRenaming={handleStartRenaming}
        archiveChat={handleArchiveChat}
        unarchiveChat={handleUnarchiveChat}
        showMoveModal={handleShowMoveModal}
        showDeleteModal={handleShowDeleteModal}
      />
      
      <DeleteModal 
        deleteModal={deleteModal}
        hideDeleteModal={hideDeleteModal}
        confirmDeleteChat={confirmDeleteChat}
      />

      <ProgressModal progressModal={progressModal} />
      <ChatSearchModal
        isOpen={isSearchOpen}
        chats={chats}
        messagesByChatId={messagesByChatId}
        onClose={() => setIsSearchOpen(false)}
        onNewChat={onNewChat}
        onChatSelect={onChatSelect}
      />
      <ProjectWorkspaceModal
        isOpen={projectModalState.isOpen}
        project={projectModalState.project}
        onClose={closeProjectModal}
        onSave={handleProjectSave}
        onDelete={onDeleteProject}
      />
      <GivingModal
        isOpen={isGivingOpen}
        onClose={() => setIsGivingOpen(false)}
      />
      <ProjectMoveModal
        isOpen={Boolean(moveModalChatId)}
        chat={movingChat}
        projects={projects}
        onClose={() => setMoveModalChatId(null)}
        onMove={onMoveChatToProject}
      />
    </div>
  );
};

export default ChatSidebar;
